import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../api';
import './ReportsPage.css';

function ReportsPage() {
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [timelineLimit, setTimelineLimit] = useState(30);
  const [failureDays, setFailureDays] = useState(30);

  const [dashboardMetrics, setDashboardMetrics] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [testCaseStatus, setTestCaseStatus] = useState([]);
  const [failureAnalysis, setFailureAnalysis] = useState([]);

  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingReports, setLoadingReports] = useState(false);
  const [error, setError] = useState('');

  const token = localStorage.getItem('token');

  const fetchReports = useCallback(async (projectId, limit, days) => {
    setLoadingReports(true);
    setError('');

    try {
      const headers = {
        Authorization: `Bearer ${token}`
      };

      const [dashboardRes, timelineRes, statusRes, failuresRes] = await Promise.all([
        fetch(`${API_BASE_URL}/reports/dashboard/${projectId}`, { headers }),
        fetch(`${API_BASE_URL}/reports/execution-timeline/${projectId}?limit=${limit}`, { headers }),
        fetch(`${API_BASE_URL}/reports/test-case-status/${projectId}`, { headers }),
        fetch(`${API_BASE_URL}/reports/failures/${projectId}?days=${days}`, { headers })
      ]);

      if (!dashboardRes.ok || !timelineRes.ok || !statusRes.ok || !failuresRes.ok) {
        throw new Error('Failed to load report data');
      }

      const [dashboardData, timelineData, statusData, failuresData] = await Promise.all([
        dashboardRes.json(),
        timelineRes.json(),
        statusRes.json(),
        failuresRes.json()
      ]);

      setDashboardMetrics(dashboardData);
      setTimeline(timelineData);
      setTestCaseStatus(statusData);
      setFailureAnalysis(failuresData);
    } catch (err) {
      console.error('Error loading reports:', err);
      setError('Failed to load reports for the selected project');
    } finally {
      setLoadingReports(false);
    }
  }, [token]);

  const fetchProjects = useCallback(async () => {
    setLoadingProjects(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/projects`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch projects');
      }

      const data = await response.json();
      setProjects(data);

      if (data.length > 0) {
        setSelectedProjectId(String(data[0].id));
      }
    } catch (err) {
      console.error('Error loading projects:', err);
      setError('Failed to load projects');
    } finally {
      setLoadingProjects(false);
    }
  }, [token]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    if (!selectedProjectId) {
      setDashboardMetrics(null);
      setTimeline([]);
      setTestCaseStatus([]);
      setFailureAnalysis([]);
      return;
    }

    fetchReports(selectedProjectId, timelineLimit, failureDays);
  }, [selectedProjectId, timelineLimit, failureDays, fetchReports]);

  const totalRecentResults = useMemo(() => {
    if (!dashboardMetrics?.recentRuns?.length) {
      return 0;
    }

    return dashboardMetrics.recentRuns.reduce((total, run) => {
      return total + Number.parseInt(run.total_results || 0, 10);
    }, 0);
  }, [dashboardMetrics]);

  const recentPassRate = useMemo(() => {
    if (!dashboardMetrics?.recentRuns?.length) {
      return 0;
    }

    const passTotal = dashboardMetrics.recentRuns.reduce((total, run) => {
      return total + Number.parseInt(run.pass_count || 0, 10);
    }, 0);

    if (!totalRecentResults) {
      return 0;
    }

    return Math.round((passTotal / totalRecentResults) * 100);
  }, [dashboardMetrics, totalRecentResults]);

  const selectedProject = useMemo(() => {
    return projects.find((project) => String(project.id) === String(selectedProjectId));
  }, [projects, selectedProjectId]);

  const timelineChart = useMemo(() => {
    const chronological = [...timeline].reverse();
    const maxTotal = Math.max(
      1,
      ...chronological.map((run) => Number.parseInt(run.pass_count || 0, 10) + Number.parseInt(run.fail_count || 0, 10))
    );

    return chronological.map((run) => {
      const passed = Number.parseInt(run.pass_count || 0, 10);
      const failed = Number.parseInt(run.fail_count || 0, 10);
      const total = passed + failed;
      return {
        id: run.id,
        name: run.name,
        heightPct: total ? Math.max(4, Math.round((total / maxTotal) * 100)) : 2,
        passFlex: passed || 0.0001,
        failFlex: failed || 0.0001,
        hasFailed: failed > 0,
        label: new Date(run.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
      };
    });
  }, [timeline]);

  const statusSummary = useMemo(() => {
    const totals = testCaseStatus.reduce((acc, tc) => {
      const executed = Number.parseInt(tc.times_executed || 0, 10);
      const pass = Number.parseInt(tc.pass_count || 0, 10);
      const fail = Number.parseInt(tc.fail_count || 0, 10);
      const blocked = Number.parseInt(tc.blocked_count || 0, 10);
      const skipped = Math.max(0, executed - pass - fail - blocked);
      acc.pass += pass;
      acc.fail += fail;
      acc.blocked += blocked;
      acc.skipped += skipped;
      return acc;
    }, { pass: 0, fail: 0, blocked: 0, skipped: 0 });

    const total = totals.pass + totals.fail + totals.blocked + totals.skipped;
    if (!total) {
      return null;
    }

    const pct = (n) => Math.round((n / total) * 100);
    return {
      passed: pct(totals.pass),
      failed: pct(totals.fail),
      blocked: pct(totals.blocked),
      skipped: pct(totals.skipped)
    };
  }, [testCaseStatus]);

  const donutGradient = useMemo(() => {
    if (!statusSummary) return '';
    const p1 = statusSummary.passed;
    const p2 = p1 + statusSummary.failed;
    const p3 = p2 + statusSummary.blocked;
    return `conic-gradient(var(--brand) 0% ${p1}%, var(--danger) ${p1}% ${p2}%, var(--warning) ${p2}% ${p3}%, var(--text-faint) ${p3}% 100%)`;
  }, [statusSummary]);

  const hasTestCases = (dashboardMetrics?.totalTestCases ?? 0) > 0;
  const hasTestRuns = (dashboardMetrics?.totalTestRuns ?? 0) > 0;
  const renderAddTestCasesState = (message) => (
    <div className="empty-state empty-state-action-row">
      <div className="empty-state-action-content">
        <p>Create test cases to get started.</p>
        <p className="empty-state-action-subtext">{message}</p>
      </div>
      <Link to="/projects" className="btn btn-primary">
        Add Test cases
      </Link>
    </div>
  );

  const renderExecutionState = (message) => (
    <div className="empty-state empty-state-action-row">
      <div className="empty-state-action-content">
        <p>No execution data yet.</p>
        <p className="empty-state-action-subtext">{message}</p>
      </div>
      <Link to="/test-execution" className="btn btn-primary">
        Go to Execution
      </Link>
    </div>
  );

  const handleRefresh = () => {
    if (!selectedProjectId) {
      return;
    }

    fetchReports(selectedProjectId, timelineLimit, failureDays);
  };

  return (
    <div className="reports-page">
      <div className="container">
        <div className="reports-header">
          <div>
            <h1>Reports</h1>
            <p className="subtitle">Track quality trends and release readiness per project.</p>
          </div>
          <button className="btn btn-secondary" onClick={handleRefresh} disabled={!selectedProjectId || loadingReports}>
            {loadingReports ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {error && <div className="error-banner">{error}</div>}

        <div className="filters">
          <div className="filter-group">
            <label htmlFor="project">Project</label>
            <select
              id="project"
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              disabled={loadingProjects}
            >
              <option value="">Select a project</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="timeline">Timeline Entries</label>
            <select
              id="timeline"
              value={timelineLimit}
              onChange={(e) => setTimelineLimit(Number.parseInt(e.target.value, 10))}
            >
              <option value={10}>10</option>
              <option value={30}>30</option>
              <option value={60}>60</option>
              <option value={90}>90</option>
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="failures">Failure Window (Days)</label>
            <select
              id="failures"
              value={failureDays}
              onChange={(e) => setFailureDays(Number.parseInt(e.target.value, 10))}
            >
              <option value={7}>7</option>
              <option value={14}>14</option>
              <option value={30}>30</option>
              <option value={90}>90</option>
            </select>
          </div>
        </div>

        {!selectedProjectId && !loadingProjects && (
          <div className="empty-state">Select a project to view reports.</div>
        )}

        {selectedProjectId && (
          <>
            <div className="metrics-grid">
              <div className="metric-card">
                <p className="metric-label">Project</p>
                <h3>{selectedProject?.name || 'Unknown'}</h3>
              </div>
              <div className="metric-card">
                <p className="metric-label">Total Test Cases</p>
                <h3>{dashboardMetrics?.totalTestCases ?? 0}</h3>
              </div>
              <div className="metric-card">
                <p className="metric-label">Total Test Runs</p>
                <h3>{dashboardMetrics?.totalTestRuns ?? 0}</h3>
              </div>
              <div className="metric-card">
                <p className="metric-label">Completed Runs</p>
                <h3>{dashboardMetrics?.completedRuns ?? 0}</h3>
              </div>
              <div className="metric-card">
                <p className="metric-label">In Progress Runs</p>
                <h3>{dashboardMetrics?.inProgressRuns ?? 0}</h3>
              </div>
              <div className="metric-card">
                <p className="metric-label">Recent Pass Rate</p>
                <h3 className="metric-value-accent">{recentPassRate}%</h3>
              </div>
            </div>

            <div className="reports-panels">
              <div className="card panel-card">
                <h3 className="panel-title">Execution Timeline</h3>
                {timelineChart.length ? (
                  <>
                    <div className="timeline-chart">
                      {timelineChart.map((day) => (
                        <div key={day.id} className="timeline-bar-col" title={day.name}>
                          <div className="timeline-bar-wrap" style={{ height: `${day.heightPct}%` }}>
                            <div className="timeline-bar-passed" style={{ flex: day.passFlex }} />
                            <div
                              className="timeline-bar-failed"
                              style={{ flex: day.failFlex, background: day.hasFailed ? 'var(--danger)' : 'transparent' }}
                            />
                          </div>
                          <span className="timeline-bar-label">{day.label}</span>
                        </div>
                      ))}
                    </div>
                    <div className="chart-legend">
                      <span className="legend-item"><span className="legend-dot" style={{ background: 'var(--brand)' }} />Passed</span>
                      <span className="legend-item"><span className="legend-dot" style={{ background: 'var(--danger)' }} />Failed</span>
                    </div>
                  </>
                ) : (
                  !hasTestCases
                    ? renderAddTestCasesState('Execution history appears after you run test cases.')
                    : renderExecutionState('Execution history appears after you start your first test run.')
                )}
              </div>

              <div className="card panel-card">
                <h3 className="panel-title">Test Case Status</h3>
                {statusSummary ? (
                  <div className="donut-row">
                    <div className="donut" style={{ backgroundImage: donutGradient }} />
                    <div className="donut-legend">
                      <span className="legend-item"><span className="legend-dot" style={{ background: 'var(--brand)' }} />Passed &middot; {statusSummary.passed}%</span>
                      <span className="legend-item"><span className="legend-dot" style={{ background: 'var(--danger)' }} />Failed &middot; {statusSummary.failed}%</span>
                      <span className="legend-item"><span className="legend-dot" style={{ background: 'var(--warning)' }} />Blocked &middot; {statusSummary.blocked}%</span>
                      <span className="legend-item"><span className="legend-dot" style={{ background: 'var(--text-faint)' }} />Skipped &middot; {statusSummary.skipped}%</span>
                    </div>
                  </div>
                ) : (
                  !hasTestCases
                    ? renderAddTestCasesState('Status breakdown appears after test runs finish.')
                    : renderExecutionState('Status breakdown appears after test runs finish.')
                )}
              </div>
            </div>

            <div className="card">
              <h3 className="panel-title">Top Failures (Last {failureDays} Days)</h3>
              {failureAnalysis.length ? (
                <div className="failures-list">
                  {failureAnalysis.map((item, idx) => (
                    <div key={`${item.name}-${idx}`} className="failure-row">
                      <div className="failure-row-main">
                        <span className="failure-name">{item.name}</span>
                        {item.error_message && <span className="failure-error">{item.error_message}</span>}
                      </div>
                      <span className="failure-count">{Number.parseInt(item.failure_count || 0, 10)} failures</span>
                    </div>
                  ))}
                </div>
              ) : (
                !hasTestCases
                  ? renderAddTestCasesState('Failure trends appear once test cases start failing.')
                  : !hasTestRuns
                    ? renderExecutionState('Failure trends appear after test runs generate result history.')
                    : <div className="empty-state">No failures found for the selected time window.</div>
              )}
            </div>

            <div className="card">
              <h3 className="panel-title">Recent Completed Runs</h3>
              {dashboardMetrics?.recentRuns?.length ? (
                <div className="table-wrapper">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Run Name</th>
                        <th>Pass</th>
                        <th>Fail</th>
                        <th>Blocked</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardMetrics.recentRuns.map((run) => (
                        <tr key={run.id}>
                          <td>{run.name}</td>
                          <td>{Number.parseInt(run.pass_count || 0, 10)}</td>
                          <td>{Number.parseInt(run.fail_count || 0, 10)}</td>
                          <td>{Number.parseInt(run.blocked_count || 0, 10)}</td>
                          <td>{Number.parseInt(run.total_results || 0, 10)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                !hasTestCases
                  ? renderAddTestCasesState('Completed runs will appear here after you execute a test run.')
                  : renderExecutionState('Completed runs will appear here after you finish a test run.')
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ReportsPage;
